import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  DashboardCard,
  Skeleton,
  cn,
} from '@zafroi/ui-kit';
import {
  Activity,
  ArrowRight,
  BookCheck,
  ClipboardCheck,
  MessageSquare,
  PenLine,
} from 'lucide-react';
import { ONBOARDING_PROMPTS } from './onboardingPrompts';
import { useBotKnowledge, useBotUsage } from '@/hooks/useEngageQueries';
import { useTenant } from '@/services/tenant-context';
import type { BotKnowledge, BotKnowledgeGap } from '@/types/engage';

/**
 * The id of a tab the `BotHealthCard` can ask its parent to switch to via the
 * `onNavigate` callback. Kept here as the canonical list so the page tab keys
 * stay aligned with what this card knows how to point at.
 */
export type ChatbotTabId = 'setup' | 'knowledge' | 'insights' | 'test' | 'settings';

/**
 * "Your bot at a glance" — the headline summary card at the top of the
 * Chatbot page. Every metric is derived from data we already fetch:
 *  - onboarding coverage from `useBotKnowledge` + ONBOARDING_PROMPTS
 *  - live answers / drafts from `useBotKnowledge`
 *  - this-month customer messages from `useBotUsage`
 *  - gaps (used only to colour the next-action line) from the last cached
 *    `getBotGaps` mutation result if the tenant ran the analysis
 */

export interface BotHealthCardProps {
  /**
   * Called when the user clicks the next-action button so the parent
   * `ChatbotPage` can switch its active tab. Optional so the card stays
   * usable in isolation (e.g. Storybook / future surfaces).
   */
  onNavigate?: (tab: ChatbotTabId) => void;
}

export function BotHealthCard({ onNavigate }: BotHealthCardProps = {}) {
  const knowledgeQuery = useBotKnowledge();
  const usageQuery = useBotUsage();
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();

  // ── Setup coverage (mirrors OnboardingChecklistSection) ──
  const { liveCount, total, setupPct } = useMemo(() => {
    const byKey = new Map<string, BotKnowledge>();
    for (const entry of knowledgeQuery.data ?? []) {
      if (entry.onboardingKey) byKey.set(entry.onboardingKey, entry);
    }
    const live = ONBOARDING_PROMPTS.reduce(
      (n, p) => (byKey.get(p.key)?.status === 'approved' ? n + 1 : n),
      0,
    );
    const t = ONBOARDING_PROMPTS.length;
    return {
      liveCount: live,
      total: t,
      setupPct: t === 0 ? 0 : Math.round((live / t) * 100),
    };
  }, [knowledgeQuery.data]);

  const approvedCount = useMemo(
    () => (knowledgeQuery.data ?? []).filter((k) => k.status === 'approved').length,
    [knowledgeQuery.data],
  );
  const draftCount = useMemo(
    () => (knowledgeQuery.data ?? []).filter((k) => k.status === 'draft').length,
    [knowledgeQuery.data],
  );

  // Gaps: we DON'T auto-fetch (the runtime's clustering pass is slow), but if
  // the tenant has run an analysis this session we pick up the cached result.
  const cachedGaps = queryClient.getQueryData<BotKnowledgeGap[]>([
    'bot-gaps',
    currentTenant.id,
  ]);
  const gapsCount = cachedGaps?.length ?? 0;

  const botTurns = usageQuery.data?.usage.botTurnsThisMonth ?? null;
  const loadingTurns = usageQuery.isLoading;

  // Pick a single next-action line based on state. The CTA label + target tab
  // travel together so the parent page can switch focus on click.
  const nextAction: {
    body: string;
    cta: { label: string; tab: ChatbotTabId } | null;
  } = (() => {
    if (setupPct < 100) {
      return {
        body: 'Finish setting up your bot — answer the remaining essentials.',
        cta: { label: 'Finish setup', tab: 'setup' },
      };
    }
    if (draftCount > 0) {
      return {
        body: `You have ${draftCount} draft${draftCount === 1 ? '' : 's'} waiting — review and approve them.`,
        cta: { label: 'Review drafts', tab: 'knowledge' },
      };
    }
    if (gapsCount > 0) {
      return {
        body: "Customers asked things your bot couldn't answer — see what to add next.",
        cta: { label: 'Fill gaps', tab: 'insights' },
      };
    }
    // "Looking good" — no CTA button (clicking the test tab is a soft link).
    return {
      body: 'Looking good — your bot is ready for customers.',
      cta: null,
    };
  })();

  return (
    <DashboardCard
      icon={Activity}
      title="Your bot at a glance"
      description="A quick read on how your bot is doing today."
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatBox
            icon={ClipboardCheck}
            label="Set up"
            value={
              knowledgeQuery.isLoading
                ? null
                : `${liveCount} of ${total}`
            }
            sub="Essential answers approved"
            valueHint={
              knowledgeQuery.isLoading
                ? undefined
                : setupPct === 100
                  ? 'Complete'
                  : `${setupPct}%`
            }
            tone={setupPct === 100 ? 'success' : 'neutral'}
          />
          <StatBox
            icon={BookCheck}
            label="Live answers"
            value={knowledgeQuery.isLoading ? null : String(approvedCount)}
            sub="What your bot can answer"
            tone="neutral"
          />
          <StatBox
            icon={PenLine}
            label="Need your input"
            value={knowledgeQuery.isLoading ? null : String(draftCount)}
            sub="Drafts waiting for review"
            tone={draftCount > 0 ? 'warning' : 'neutral'}
          />
          <StatBox
            icon={MessageSquare}
            label="This month"
            value={
              loadingTurns
                ? null
                : botTurns != null
                  ? botTurns.toLocaleString()
                  : '—'
            }
            sub="Customer messages handled"
            tone="neutral"
          />
        </div>

        <div className="flex items-start gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
          <Badge tone="info" withDot={false}>
            Next
          </Badge>
          <span className="flex-1 pt-0.5 text-foreground">{nextAction.body}</span>
          {nextAction.cta && onNavigate && (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => onNavigate(nextAction.cta!.tab)}
            >
              {nextAction.cta.label}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </DashboardCard>
  );
}

interface StatBoxProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** `null` while loading — renders a skeleton. */
  value: string | null;
  sub: string;
  /** A small inline qualifier (e.g. percent) shown beside the value. */
  valueHint?: string;
  tone: 'neutral' | 'success' | 'warning';
}

function StatBox({
  icon: Icon,
  label,
  value,
  sub,
  valueHint,
  tone,
}: StatBoxProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-lg border bg-card p-3',
        tone === 'warning'
          ? 'border-warning/40 bg-warning/5'
          : tone === 'success'
            ? 'border-success/30 bg-success/5'
            : 'border-border',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          <Icon className="h-3 w-3" />
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        {value === null ? (
          <Skeleton className="h-6 w-16" />
        ) : (
          <span
            className={cn(
              'text-2xl font-semibold tracking-tight',
              tone === 'warning' ? 'text-warning' : 'text-foreground',
            )}
          >
            {value}
          </span>
        )}
        {valueHint && (
          <span className="text-xs text-muted-foreground">{valueHint}</span>
        )}
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">{sub}</p>
    </div>
  );
}
